import { useEffect, useRef, useState } from 'react'
import { ExternalLink } from '@digital-twins/design-system/icons'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

const FRAME_BLOCK_DETECT_MS = 2500

interface WebsitePreviewProps {
  url: string
  title: string
  thumbnailUrl?: string | null
  fallbackText?: string | null
  className?: string
}

export function isEmbeddableHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Sites that refuse framing leave the iframe on about:blank, which is
 * same-origin so we can read it. A real cross-origin page throws instead —
 * that throw is the success signal.
 */
export function isFramedPageBlocked(iframe: HTMLIFrameElement): boolean {
  try {
    const href = iframe.contentWindow?.location.href ?? ''
    return href === '' || href === 'about:blank'
  } catch {
    return false
  }
}

export function WebsitePreview(props: WebsitePreviewProps) {
  const { url, title, thumbnailUrl, fallbackText, className } = props

  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const [blocked, setBlocked] = useState(false)

  const embeddable = isEmbeddableHttpUrl(url)

  useEffect(() => {
    setBlocked(!embeddable)
    if (!embeddable) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      const iframe = iframeRef.current
      if (!iframe || isFramedPageBlocked(iframe)) {
        setBlocked(true)
      }
    }, FRAME_BLOCK_DETECT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [url, embeddable])

  function handleLoad() {
    const iframe = iframeRef.current
    if (!iframe) {
      return
    }
    try {
      const href = iframe.contentWindow?.location.href ?? ''
      if (href && href !== 'about:blank') {
        setBlocked(false)
      }
    } catch {
      setBlocked(false)
    }
  }

  const openWebsiteButton = (
    <Button asChild variant="outline" size="sm" className="h-8">
      <a
        href={embeddable ? url : undefined}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="website-preview-open"
      >
        <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
        Open website
      </a>
    </Button>
  )

  return (
    <div
      className={cn(
        'relative flex min-h-[70vh] flex-1 flex-col overflow-hidden rounded-xl border border-border bg-white',
        className,
      )}
      data-testid="website-preview"
    >
      {embeddable ? (
        <iframe
          key={url}
          ref={iframeRef}
          src={url}
          title={title}
          className="h-full min-h-[70vh] w-full bg-white"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={handleLoad}
          data-testid="website-preview-frame"
        />
      ) : null}
      {blocked ? (
        <div
          className="absolute inset-0 overflow-y-auto bg-background p-4 md:p-6"
          data-testid="website-preview-fallback"
        >
          <div className="mx-auto flex max-w-3xl flex-col gap-4">
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt=""
                className="max-h-48 w-full rounded-xl object-cover"
                data-testid="website-preview-thumbnail"
              />
            ) : null}
            <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
              <Text size="sm">
                This website does not allow embedding in the preview. Open it
                in a new tab to see the original page.
              </Text>
              {openWebsiteButton}
            </div>
            {fallbackText ? (
              <div className="flex flex-col gap-3 rounded-xl border border-border bg-white p-6 text-black shadow-sm dark:bg-zinc-100">
                <Text size="xs" color="muted">
                  Captured text
                </Text>
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-zinc-800">
                  {fallbackText}
                </pre>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}
